import { useState } from "react";
import { Lock, CheckCircle2, Mail, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "../lib/api";
import { track, events } from "../lib/analytics";

/**
 * Iter-185.BM — Audit Finding #7: Email Capture Wall on Calculator Results.
 *
 * Audit problem (`/app/memory/AUDIT_2026-02.md` TIER 2 #7):
 *   The savings + refinance calculators display results unconditionally.
 *   Drivers walk away with the savings number; we lose the lead.
 *
 * Audit solution:
 *   - Show 60% of the breakdown immediately (preserved upstream — this
 *     wall sits BELOW the live results, never hides the top-line number).
 *   - Gate the remaining "full savings playbook" behind a soft email
 *     capture (this component).
 *   - 7-email drip after capture (handled by the existing
 *     `_newsletter_signup_to_drip_category` dispatcher on the backend —
 *     `source_cluster: "savings-playbook"` routes to the refinance/
 *     insurance triggers series).
 *
 * Revenue model: ~12K new subscribers/yr × $8 LTV ≈ $96K/yr direct.
 *
 * UX rules:
 *   - We do NOT hide the user's calculated number. The audit wording
 *     ("show 60%") refers to the playbook content, not the savings
 *     headline. Hiding their number would destroy trust + violate the
 *     truthfulness mandate.
 *   - 3 of 5 action steps render unblurred so the value is visible
 *     before the gate.
 *   - Steps 4–5 sit behind a single "Unlock the rest →" email gate
 *     that reuses the existing `/api/newsletter` Resend pipeline.
 */

// 5-step playbook templates per vertical. Step IDs map to the drip
// content we send post-signup so the user gets the same advice they
// were shown the preview of.
const PLAYBOOKS = {
  insurance: {
    eyebrow: "Insurance savings playbook",
    headline: "5 actions that capture your projected savings",
    steps: [
      "Quote 4+ insurers in one 2-minute sitting — re-shop volume drives the savings, not loyalty discounts.",
      "Bundle home or renters with auto — typical stacked discount is 8–15%.",
      "Raise your deductible to $1,000 if your savings cushion can absorb it — annual premium drop of 9–14%.",
      "Audit your mileage band — drivers commuting <7,500 mi/yr should be in a lower-tier band, but ~30% are mis-classified.",
      "Stack telematics + safe-driver discounts — the carrier-specific layering is the play.",
    ],
  },
  refinance: {
    eyebrow: "Refinance savings playbook",
    headline: "5 triggers that signal it's time to refinance",
    steps: [
      "Your FICO is up 50+ points since you bought — the single strongest predictor of an APR drop ≥ 2 points.",
      "Market rates dropped ≥ 1 point since your origination date.",
      "You have 24+ months left on the loan — the break-even window for closing costs.",
      "Your current APR is ≥ 7.5% AND your credit score is ≥ 670 — you're overpaying for your risk tier.",
      "You want to remove a co-signer or change the loan term — refi is the cleanest path.",
    ],
  },
  combined: {
    eyebrow: "Personalized savings playbook",
    headline: "5 moves to actually capture your projected savings",
    steps: [
      "Re-shop your insurance with 4+ carriers in one sitting — drives 80% of the insurance lane savings.",
      "Pre-qualify with 3 refinance lenders via soft pull — no impact to your credit score.",
      "Audit your insurance mileage band + deductible — two settings most drivers never revisit.",
      "Run the refi break-even — closing costs vs. monthly savings × remaining months.",
      "Lock both wins in writing before your renewal date — the savings vanish if you miss the window.",
    ],
  },
};

export default function CalculatorPlaybookWall({
  category = "combined",
  estimatedAnnual = 0,
  sourcePage = "calculator",
  variant = "dark",
  onSkip,
}) {
  const [email, setEmail] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [saving, setSaving] = useState(false);

  const cfg = PLAYBOOKS[category] || PLAYBOOKS.combined;
  const visibleSteps = cfg.steps.slice(0, 3);
  const gatedSteps = cfg.steps.slice(3);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setSaving(true);
    try {
      await subscribeNewsletter(trimmed, {
        source_cluster: "savings-playbook",
        source_path:
          typeof window !== "undefined" ? window.location.pathname : undefined,
      });
      setUnlocked(true);
      track(events.EMAIL_CAPTURE_SUBMITTED, {
        category,
        source: sourcePage,
        surface: "calculator_playbook_wall",
        estimated_annual: estimatedAnnual,
      });
      toast.success("Playbook unlocked — full series sent to your inbox.");
    } catch {
      toast.error("Couldn't save — try again later.");
    } finally {
      setSaving(false);
    }
  };

  // Iter-190.G — Skip dismisses the playbook entirely (parent
  // un-mounts via the `onSkip` callback). Previously the click only
  // logged an analytics event with no visible effect, which read as
  // broken UX. The callback bubbles up to SavingsCalculator's
  // `playbookDismissed` state, which guards the wrapping
  // <div data-testid="savings-playbook-block"> so the entire row-4
  // container shrinks away.
  const handleSkip = () => {
    track(events.EMAIL_CAPTURE_SKIPPED, {
      category,
      source: sourcePage,
      surface: "calculator_playbook_wall",
    });
    if (typeof onSkip === "function") {
      onSkip();
    }
  };

  // Color tokens by variant so the wall reads correctly on the dark
  // homepage savings section (variant=dark) and the light-bg refi
  // calculator page (variant=light).
  const isDark = variant === "dark";
  const containerCls = isDark
    ? "border border-white/15 bg-white/[0.04]"
    : "border border-slate-200 bg-white";
  const headlineCls = isDark ? "text-white" : "text-slate-950";
  const eyebrowCls = isDark ? "text-[#10B981]" : "text-emerald-700";
  const bodyCls = isDark ? "text-slate-300" : "text-slate-600";
  const stepNumCls = isDark
    ? "bg-[#10B981]/15 text-[#10B981]"
    : "bg-emerald-50 text-emerald-700";
  const stepTextCls = isDark ? "text-slate-200" : "text-slate-700";
  const inputCls = isDark
    ? "bg-white text-slate-900 border border-white/30"
    : "bg-white text-slate-900 border border-slate-300";

  return (
    <div
      data-testid="calculator-playbook-wall"
      data-category={category}
      data-unlocked={unlocked ? "true" : "false"}
      className={`mt-8 p-6 md:p-7 ${containerCls}`}
    >
      <p
        className={`text-[10px] font-bold uppercase tracking-[0.22em] mb-2 ${eyebrowCls}`}
      >
        <Sparkles className="inline w-3 h-3 mr-1" /> {cfg.eyebrow}
      </p>
      <h3
        data-testid="calculator-playbook-headline"
        className={`font-display font-black text-2xl md:text-3xl tracking-tight leading-tight mb-5 ${headlineCls}`}
      >
        {cfg.headline}
      </h3>

      <ol className="space-y-3 mb-5">
        {visibleSteps.map((step, i) => (
          <li
            key={`visible-${i}`}
            data-testid={`calculator-playbook-step-${i + 1}`}
            className="flex items-start gap-3"
          >
            <span
              className={`shrink-0 w-7 h-7 grid place-items-center text-xs font-bold tabular-nums ${stepNumCls}`}
            >
              {i + 1}
            </span>
            <p className={`text-sm leading-relaxed ${stepTextCls}`}>{step}</p>
          </li>
        ))}

        {gatedSteps.map((step, i) => (
          <li
            key={`gated-${i}`}
            data-testid={`calculator-playbook-gated-step-${i + 4}`}
            data-locked={unlocked ? "false" : "true"}
            className="flex items-start gap-3"
          >
            <span
              className={`shrink-0 w-7 h-7 grid place-items-center text-xs font-bold tabular-nums ${stepNumCls}`}
            >
              {i + 4}
            </span>
            {unlocked ? (
              <p className={`text-sm leading-relaxed ${stepTextCls}`}>{step}</p>
            ) : (
              <p
                className={`text-sm leading-relaxed italic flex items-center gap-2 ${bodyCls}`}
              >
                <Lock className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} />
                <span className="select-none blur-sm">{step.slice(0, 60)}…</span>
              </p>
            )}
          </li>
        ))}
      </ol>

      {!unlocked ? (
        <form
          data-testid="calculator-playbook-form"
          onSubmit={handleSubmit}
          className={`pt-5 border-t ${isDark ? "border-white/10" : "border-slate-200"}`}
        >
          <div
            className={`flex items-start gap-2 mb-3 text-xs leading-relaxed ${bodyCls}`}
          >
            <Mail
              className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${isDark ? "text-[#10B981]" : "text-emerald-700"}`}
              strokeWidth={2.5}
            />
            <span>
              <strong className={isDark ? "text-white" : "text-slate-950"}>
                Unlock the remaining steps + the full 7-email playbook.
              </strong>{" "}
              We&apos;ll send the complete action plan + a refinance
              break-even worksheet. No spam — unsubscribe anytime.
            </span>
          </div>
          {/* Iter-190.F — Constrain the email form to a comfortable
              max-w so the input doesn't sprawl across the full-width
              playbook card on wide desktops (read as awkwardly long
              when the wall went full-width below the calculator). */}
          <div className="max-w-xl">
            <div className="flex flex-col sm:flex-row gap-2.5">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                autoComplete="email"
                disabled={saving}
                aria-label="Email address"
                data-testid="calculator-playbook-input"
                className={`w-full sm:flex-1 h-12 px-3.5 text-sm focus:outline-none focus:border-[#10B981] ${inputCls}`}
              />
              <button
                type="submit"
                disabled={saving || !email.trim()}
                data-testid="calculator-playbook-submit"
                className="press inline-flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#34D399] disabled:bg-slate-300 disabled:cursor-not-allowed text-[#0A2540] font-bold h-12 px-6 text-xs uppercase tracking-[0.16em] whitespace-nowrap"
              >
                {saving ? "Unlocking…" : "Unlock the rest"}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <button
              type="button"
              onClick={handleSkip}
              data-testid="calculator-playbook-skip"
              className={`mt-3 text-[11px] uppercase tracking-[0.18em] font-semibold underline-offset-4 hover:underline ${bodyCls}`}
            >
              Skip for now
            </button>
          </div>
        </form>
      ) : (
        <div
          data-testid="calculator-playbook-captured"
          className={`pt-5 border-t flex items-start gap-2.5 ${isDark ? "border-white/10" : "border-slate-200"}`}
        >
          <CheckCircle2
            className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5"
            strokeWidth={2.5}
          />
          <div>
            <p
              className={`text-sm font-bold leading-tight ${headlineCls}`}
            >
              Playbook unlocked + 7-email series queued.
            </p>
            <p className={`text-xs mt-1 leading-relaxed ${bodyCls}`}>
              First email lands in your inbox shortly. Check spam if you
              don&apos;t see it within 5 minutes.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
